import { create } from "zustand";
import i18n from "@/i18n";

export type Theme = "light" | "dark" | "system";
export type Language = "fr" | "en" | "ar";
export type Accent = "indigo" | "violet" | "blue" | "green" | "amber";

export const ACCENTS: Accent[] = ["indigo", "violet", "blue", "green", "amber"];

const THEME_KEY = "fad.theme";
const LANG_KEY = "fad.lang";
const ACCENT_KEY = "fad.accent";

function systemPrefersDark(): boolean {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? true;
}

/** Apply the resolved theme to <html> (data-theme). */
function applyTheme(theme: Theme): void {
  const resolved = theme === "system" ? (systemPrefersDark() ? "dark" : "light") : theme;
  document.documentElement.setAttribute("data-theme", resolved);
}

/** Apply the language to <html> (lang, dir) and switch i18next. */
function applyLanguage(lang: Language): void {
  document.documentElement.setAttribute("lang", lang);
  document.documentElement.setAttribute("dir", lang === "ar" ? "rtl" : "ltr");
  void i18n.changeLanguage(lang);
}

/** Apply the accent color to <html> (data-accent). Indigo is the default token set. */
function applyAccent(accent: Accent): void {
  document.documentElement.setAttribute("data-accent", accent);
}

function storedAccent(): Accent {
  const v = localStorage.getItem(ACCENT_KEY) as Accent | null;
  return v && ACCENTS.includes(v) ? v : "indigo";
}

interface UiState {
  theme: Theme;
  language: Language;
  accent: Accent;
  sidebarOpen: boolean;
  notificationsOpen: boolean;
  setTheme: (theme: Theme) => void;
  setLanguage: (lang: Language) => void;
  setAccent: (accent: Accent) => void;
  toggleSidebar: () => void;
  setNotificationsOpen: (open: boolean) => void;
  init: () => void;
}

export const useUiStore = create<UiState>((set, get) => ({
  theme: (localStorage.getItem(THEME_KEY) as Theme) ?? "dark",
  language: (localStorage.getItem(LANG_KEY) as Language) ?? "fr",
  accent: storedAccent(),
  sidebarOpen: false,
  notificationsOpen: false,

  setTheme: (theme) => {
    localStorage.setItem(THEME_KEY, theme);
    applyTheme(theme);
    set({ theme });
  },
  setLanguage: (language) => {
    localStorage.setItem(LANG_KEY, language);
    applyLanguage(language);
    set({ language });
  },
  setAccent: (accent) => {
    localStorage.setItem(ACCENT_KEY, accent);
    applyAccent(accent);
    set({ accent });
  },
  toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
  setNotificationsOpen: (notificationsOpen) => set({ notificationsOpen }),

  init: () => {
    applyTheme(get().theme);
    applyLanguage(get().language);
    applyAccent(get().accent);
  },
}));
